import { useState, useEffect } from 'react';

import BarcodeScanner from './components/BarcodeScanner';
import PriceEntryModal from './components/PriceEntryModal';
import PricesFoundModal from './components/PricesFoundModal';

import DashboardHeader from './components/dashboard/DashboardHeader';
import ScannerCTA from './components/dashboard/ScannerCTA';
import ScanHistory from './components/dashboard/ScanHistory';
import FeatureCards from './components/dashboard/FeatureCards';
import ImpactStats from './components/dashboard/ImpactStats';
import StatsFooter from './components/dashboard/StatsFooter';
import WantedListPanel from './components/dashboard/WantedListPanel';
import BatchCheckerPanel from './components/dashboard/BatchCheckerPanel';
import WelcomeBanner from './components/dashboard/WelcomeBanner';
import AssistantWidget from './components/assistant/AssistantWidget';

import useDashboardData from './hooks/useDashboardData';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Dashboard shell — owns scan-flow orchestration + modal state.
 * Auth & profile loading delegated to `useDashboardData`.
 * Pure-render sections live in `/components/dashboard/*`.
 */
export default function Dashboard() {
  const {
    user,
    isLoading,
    userBalance,
    totalEarnings,
    totalChecks,
    scannedItems,
    loadScanHistory,
    applyEarnings,
    logout,
  } = useDashboardData();

  // Scan + modal state owned here because it straddles several children.
  const [showScanner, setShowScanner] = useState(false);
  const [continuousMode, setContinuousMode] = useState(false);
  const [showPriceEntry, setShowPriceEntry] = useState(false);
  const [currentBarcode, setCurrentBarcode] = useState(null);
  const [pricesFoundData, setPricesFoundData] = useState(null);

  // Public stats (footer) — refreshed once on mount.
  const [publicStats, setPublicStats] = useState(null);
  useEffect(() => {
    fetch(`${API}/api/public/stats`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => d && setPublicStats(d))
      .catch(() => {});
  }, []);

  // ---------- Handlers ----------
  const handleScanComplete = async (barcodeData) => {
    try {
      const mediaRes = await fetch(`${API}/api/scans/media/${barcodeData.barcode}`, { credentials: 'include' });
      const mediaData = await mediaRes.json();

      if (mediaData.found && (mediaData.status === 'has_prices' || mediaData.status === 'verified')) {
        setPricesFoundData({ barcode: barcodeData.barcode, prices: mediaData.prices });
        if (!continuousMode) setShowScanner(false);
      } else {
        setCurrentBarcode(barcodeData.barcode);
        setShowScanner(false);
        setShowPriceEntry(true);
      }
    } catch (err) {
      console.error('[Dashboard] Error checking barcode:', err);
      setCurrentBarcode(barcodeData.barcode);
      setShowScanner(false);
      setShowPriceEntry(true);
    }

    // Always persist the scan.
    try {
      await fetch(`${API}/api/scans/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ barcode: barcodeData.barcode, type: barcodeData.type, raw: barcodeData.raw }),
      });
      loadScanHistory();
    } catch (err) {
      console.error('[Dashboard] Error saving scan:', err);
    }
  };

  const handlePriceSubmit = async (priceData) => {
    try {
      const res = await fetch(`${API}/api/prices/manual`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(priceData),
      });
      const data = await res.json();

      if (res.ok) {
        applyEarnings(data);
        alert(`Success! You earned £${data.earnings.toFixed(2)}\nNew balance: £${data.total_balance.toFixed(2)}`);
        setShowPriceEntry(false);
        setCurrentBarcode(null);
        if (continuousMode) setShowScanner(true);
      } else {
        alert('Error submitting prices. Please try again.');
      }
    } catch (err) {
      console.error('Error submitting prices:', err);
      alert('Network error. Please try again.');
    }
  };

  const handlePriceSkip = async () => {
    try {
      await fetch(`${API}/api/scans/skip-prices`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ barcode: currentBarcode }),
      });
    } catch (err) {
      console.error('[Dashboard] Error saving skipped barcode:', err);
    }
    setShowPriceEntry(false);
    setCurrentBarcode(null);
    if (continuousMode) setShowScanner(true);
  };

  // ---------- Guards ----------
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-950">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#00D26A] mx-auto"></div>
          <p className="mt-4 text-zinc-400">Loading...</p>
        </div>
      </div>
    );
  }
  if (!user) return null;

  // ---------- Render ----------
  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-200">
      <DashboardHeader user={user} userBalance={userBalance} onLogout={logout} />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <ScannerCTA
          continuousMode={continuousMode}
          setContinuousMode={setContinuousMode}
          onScanClick={() => setShowScanner(true)}
        />

        <ScanHistory scannedItems={scannedItems} />
        <WantedListPanel user={user} />
        <BatchCheckerPanel />
        <WelcomeBanner />
        <FeatureCards totalChecks={totalChecks} totalEarnings={totalEarnings} />
        <ImpactStats
          scannedItemsCount={scannedItems.length}
          totalChecks={totalChecks}
          totalEarnings={totalEarnings}
          userBalance={userBalance}
        />
      </main>
      <AssistantWidget />

      {showScanner && (
        <BarcodeScanner
          onScan={handleScanComplete}
          onClose={() => setShowScanner(false)}
          continuousMode={continuousMode}
        />
      )}

      {showPriceEntry && currentBarcode && (
        <PriceEntryModal
          barcode={currentBarcode}
          onSubmit={handlePriceSubmit}
          onSkip={handlePriceSkip}
          onClose={() => setShowPriceEntry(false)}
        />
      )}

      {pricesFoundData && (
        <PricesFoundModal
          barcode={pricesFoundData.barcode}
          prices={pricesFoundData.prices}
          onClose={() => setPricesFoundData(null)}
        />
      )}

      <StatsFooter publicStats={publicStats} />
    </div>
  );
}
